/**
 * 詳細レポートの「解析時の設定」表示 (docs/27 §15-5 C12)。
 * 新方式で解析した地点は解析時の設定の記録を出し、現在の設定と違えば再解析が必要と示す。
 */
import { Card } from '@citizen-walk/shared-ui';
import {
  diffStreetSurveyExtractionRecord,
  STREET_SURVEY_DIRECTION_LABELS,
  type StreetSurveyExtractionRecord,
} from '@citizen-walk/shared-types';
import type { StreetSurveyConfig } from './useStreetSurveyConfig';

export interface StreetSurveyExtractionRecordCardProps {
  record: StreetSurveyExtractionRecord | undefined;
  config: StreetSurveyConfig;
}

export function StreetSurveyExtractionRecordCard({
  record,
  config,
}: StreetSurveyExtractionRecordCardProps) {
  const current = config.extraction;
  if (!record && !current.enabled) return null;
  const diffs = diffStreetSurveyExtractionRecord(record, current, config.mastersById);
  const labelOf = (codeField: string) =>
    current.candidates.find((a) => a.codeField === codeField)?.label ?? '（現在の項目に無い項目）';

  return (
    <Card padding="md" className="mb-3">
      <h2 className="text-sm font-semibold text-slate-800 mb-1">解析時の設定</h2>
      {diffs.length > 0 && (
        <p className="mb-2 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-800">
          現在の解析の設定と違います（{diffs.join('、')}
          ）。この地点を再解析すると、現在の設定で解析し直します。
        </p>
      )}
      {record ? (
        <dl className="grid grid-cols-1 gap-x-4 gap-y-1 text-xs text-slate-700 sm:grid-cols-2">
          <div>
            <dt className="inline text-slate-500">位置を検出する項目: </dt>
            <dd className="inline">{labelOf(record.anchorCodeField)}</dd>
          </div>
          <div>
            <dt className="inline text-slate-500">各件に付ける項目: </dt>
            <dd className="inline">
              {record.attachedCodeFields.length > 0
                ? record.attachedCodeFields.map(labelOf).join('、')
                : 'なし'}
            </dd>
          </div>
          <div>
            <dt className="inline text-slate-500">件数: </dt>
            <dd className="inline">
              1 方向 {record.perDirectionMaxItems} 件まで ／ 地点全体{' '}
              {record.totalMaxItems !== null ? `${record.totalMaxItems} 件まで` : '絞らない'}（候補{' '}
              {record.candidateCount} 件）
            </dd>
          </div>
          <div>
            <dt className="inline text-slate-500">4 方向の選抜: </dt>
            <dd className="inline">
              {record.selection === 'done'
                ? '実施'
                : '失敗したため方向ごとに均等に残しました（説明文なし）'}
            </dd>
          </div>
          <div>
            <dt className="inline text-slate-500">解析できなかった方向: </dt>
            <dd className="inline">
              {record.missingDirections.length > 0
                ? record.missingDirections
                    .map((d) => STREET_SURVEY_DIRECTION_LABELS[d] ?? d)
                    .join('、')
                : 'なし'}
            </dd>
          </div>
          <div>
            <dt className="inline text-slate-500">AI モデル: </dt>
            <dd className="inline">
              {record.model}
              {record.analyzedWith === 'text' ? '（出力の型の指定なしで解析）' : ''}
            </dd>
          </div>
        </dl>
      ) : (
        <p className="text-xs text-slate-600">この地点は従来の方式で解析されています。</p>
      )}
    </Card>
  );
}
